<template>
  <div class="relative">
    <!-- Botón de notificaciones -->
    <button 
      @click="toggleNotificationsMenu" 
      class="relative group flex items-center justify-center p-2 rounded-lg hover:bg-indigo-50 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      aria-label="Notificaciones"
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        class="h-6 w-6 text-gray-500 group-hover:text-indigo-600 transition-all duration-200" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
        stroke-width="2"
      >
        <path 
          stroke-linecap="round" 
          stroke-linejoin="round" 
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" 
        />
      </svg>
      
      <!-- Contador de notificaciones -->
      <span 
        v-if="unreadCount > 0" 
        class="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white"
      >
        {{ unreadCount > 9 ? '9+' : unreadCount }}
      </span>
    </button>

    <!-- Menú desplegable de notificaciones -->
    <div 
      v-if="isMenuOpen" 
      class="fixed z-50 inset-x-4 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 sm:absolute sm:w-80 sm:right-0 sm:top-auto sm:mt-2 sm:inset-x-auto sm:left-auto sm:max-w-none sm:translate-x-0"
    >
      <div class="px-4 py-3 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold text-gray-900">Notificaciones</h3>
          <button 
            v-if="notifications.length > 0"
            @click="markAllAsRead"
            class="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Marcar todas como leídas
          </button>
        </div>
      </div>
      
      <div class="max-h-80 overflow-y-auto">
        <!-- Sin notificaciones -->
        <div v-if="notifications.length === 0" class="px-4 py-6 text-center">
          <p class="text-sm text-gray-500">No tienes notificaciones</p>
        </div>
        
        <!-- Lista de notificaciones -->
        <div v-else>
          <div 
            v-for="notification in notifications" 
            :key="notification.id" 
            class="px-4 py-3 hover:bg-gray-50 transition-colors"
            :class="{'bg-indigo-50': !notification.read}"
          >
            <div class="flex items-start">
              <!-- Icono según tipo de notificación (solo para contratos tradicionales) -->
              <div v-if="!isColivingNotification(notification)" class="flex-shrink-0 mr-3">
                <div class="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  <svg v-if="notification.type === 'upload'" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <svg v-else-if="notification.type === 'interest'" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  <svg v-else-if="notification.type === 'assigned'" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <svg v-else-if="notification.type === 'signed'" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  <svg v-else-if="notification.type === 'payment'" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              
              <!-- Contenido de la notificación -->
              <div class="flex-1 min-w-0" :class="{ 'ml-3': !isColivingNotification(notification) }">
                <p class="text-sm font-medium text-gray-900">
                  {{ notification.message }}
                </p>
                <p class="text-xs text-gray-500 mt-1">
                  {{ formatDate(notification.timestamp) }}
                </p>
                <!-- Información adicional para contratos tradicionales -->
                <p v-if="notification.contractTitle && !isColivingNotification(notification)" 
                   class="text-xs text-indigo-600 mt-1 truncate">
                  {{ notification.contractTitle }}
                </p>
                <!-- Información adicional para colivings -->
                <div v-if="isColivingNotification(notification)" class="mt-1">
                  <p class="text-xs text-purple-600 truncate">
                    {{ getColivingTitle(notification) }}
                  </p>
                  <p v-if="getNotificationTypeText(notification.type)" 
                     class="text-xs text-gray-400 mt-0.5">
                    {{ getNotificationTypeText(notification.type) }}
                  </p>
                </div>
              </div>
              
              <!-- Indicador de no leído -->
              <div v-if="!notification.read" class="flex-shrink-0 ml-2">
                <span class="h-2 w-2 rounded-full bg-indigo-600"></span>
              </div>
            </div>
            
            <!-- Acciones -->
            <div class="flex justify-end mt-2 space-x-2">
              <button 
                @click.stop="markAsRead(notification)"
                class="text-xs text-gray-600 hover:text-gray-800 cursor-pointer" 
                v-if="!notification.read"
              >
                Marcar como leída
              </button>
              <button 
                @click.stop="deleteNotification(notification.id)"
                class="text-xs text-red-500 hover:text-red-700 cursor-pointer"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Pie del menú con enlace a todas las notificaciones -->
      <div v-if="notifications.length > 0" class="px-4 py-2 bg-gray-50">
        <button 
          @click="closeMenu"
          class="w-full text-center text-xs text-gray-500 hover:text-gray-700"
        >
          Cerrar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { formatDistance } from 'date-fns'
import { es } from 'date-fns/locale'

export default {
  name: 'NotificationsMenu',
  props: {
    notifications: {
      type: Array,
      default: () => []
    }
  },
  emits: ['mark-read', 'mark-all-read', 'delete', 'view'],
  setup(props, { emit }) {
    const router = useRouter()
    const isMenuOpen = ref(false)
    
    // Contador de notificaciones no leídas
    const unreadCount = computed(() => {
      return props.notifications.filter(notification => !notification.read).length
    })
    
    // Alternar estado del menú de notificaciones
    const toggleNotificationsMenu = () => {
      isMenuOpen.value = !isMenuOpen.value
    }
    
    // Cerrar menú
    const closeMenu = () => {
      isMenuOpen.value = false
    }
    
    // Formatear fecha relativa
    const formatDate = (timestamp) => {
      if (!timestamp) return ''
      
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return formatDistance(date, new Date(), { addSuffix: true, locale: es })
    }
    
    // Marcar notificación como leída
    const markAsRead = (notification) => {
      emit('mark-read', notification.id)
    }
    
    // Marcar todas las notificaciones como leídas
    const markAllAsRead = () => {
      emit('mark-all-read')
    }
    
    // Eliminar notificación
    const deleteNotification = (notificationId) => {
      emit('delete', notificationId)
    }
    
    // Verificar si es una notificación de coliving
    const isColivingNotification = (notification) => {
      const colivingTypes = [
        'coliving_application_submitted',
        'coliving_pre_approved',
        'coliving_applicant_signed',
        'coliving_contract_completed',
        'coliving_payment_completed',
        'coliving_assignment_confirmed'
      ]
      return colivingTypes.includes(notification.type)
    }
    
    // Obtener título del coliving
    const getColivingTitle = (notification) => {
      return notification.contractTitle || notification.data?.colivingTitle || 'Coliving'
    }
    
    // Obtener texto descriptivo del tipo de notificación
    const getNotificationTypeText = (type) => {
      const typeTexts = {
        'coliving_application_submitted': 'Solicitud de coliving',
        'coliving_pre_approved': 'Pre-aprobación',
        'coliving_applicant_signed': 'Firma de inquilino',
        'coliving_contract_completed': 'Contrato completado',
        'coliving_payment_completed': 'Pago completado',
        'coliving_assignment_confirmed': 'Asignación confirmada'
      }
      return typeTexts[type] || ''
    }
    
    // Ver detalles de la notificación (solo para eventos, sin navegación)
    const viewNotification = (notification) => {
      // Solo marcar como leída y emitir evento, sin navegación
      if (!notification.read) {
        markAsRead(notification)
      }
      
      emit('view', notification)
    }
    
    // Cerrar menú al hacer clic fuera
    const handleClickOutside = (event) => {
      if (isMenuOpen.value) {
        const menu = document.querySelector('.notifications-menu')
        if (menu && !menu.contains(event.target)) {
          isMenuOpen.value = false
        }
      }
    }
    
    // Configurar y limpiar listeners
    onMounted(() => {
      document.addEventListener('mousedown', handleClickOutside)
    })
    
    onUnmounted(() => {
      document.removeEventListener('mousedown', handleClickOutside)
    })
    
    return {
      isMenuOpen,
      unreadCount,
      toggleNotificationsMenu,
      closeMenu,
      formatDate,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      viewNotification,
      isColivingNotification,
      getColivingTitle,
      getNotificationTypeText
    }
  }
}
</script>

<style scoped>
.max-h-80 {
  max-height: 20rem;
}
</style> 